import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sparkles,
  RefreshCw,
  ChevronRight,
  User,
  FileText,
  Package,
  Tag,
  Loader2,
  PenTool,
  Target,
  TrendingUp,
  Search,
  Lightbulb,
  X,
  FolderTree,
  Plus,
  CheckCircle,
  Edit,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from 'react-hot-toast';
import { AIWriterPreview } from './AIWriterPreview';

interface PersonaArticleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona: {
    id?: string;
    name: string;
    description?: string;
    characteristics?: string[];
    userCount?: number;
    percentage?: number;
    avgOrderValue?: number;
    conversionRate?: number;
    // Buyer Persona fields from The Sector
    occupation?: string;
    age_range?: string;
    buying_behavior?: string;
    expectations?: string;
    pain_points?: string;
    preferred_features?: string[];
  };
  siteId: string;
}

interface TopicSuggestion {
  id: string;
  title: string;
  description: string;
  relevanceScore: number;
  keywords: string[];
  targetIntent: string;
  relatedCategories?: string[]; // Categories this topic is related to
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image_url?: string;
}

interface SuggestedProduct {
  id: string;
  name: string;
  category: string;
  relevance: number;
  matchReason?: string;
}

interface SuggestedCategory {
  name: string;
  relevance: number;
  matchReason?: string;
}

interface CategoryWithCount {
  name: string;
  productCount: number;
}

interface AEOContext {
  products: Array<{
    id: string;
    name: string;
    enrichedAttributes?: Record<string, any>;
    benefitScores?: Record<string, number>;
  }>;
  categories: Array<{
    name: string;
    definition?: string;
    benefits?: string[];
    questions?: string[];
  }>;
}

export function AIWriterPersonaModal({
  open,
  onOpenChange,
  persona,
  siteId,
}: PersonaArticleModalProps) {
  const [step, setStep] = useState<'topics' | 'config' | 'generating'>('topics');
  const [loading, setLoading] = useState(false);
  const [suggestedTopics, setSuggestedTopics] = useState<TopicSuggestion[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<TopicSuggestion | null>(null);
  const [existingArticles, setExistingArticles] = useState<any[]>([]);
  
  // Configuration state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoriesWithCounts, setCategoriesWithCounts] = useState<CategoryWithCount[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [targetKeywords, setTargetKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [articleLength, setArticleLength] = useState('1500');
  const [tone, setTone] = useState('professional');
  const [perspective, setPerspective] = useState('second-person');
  const [ctaStyle, setCtaStyle] = useState('soft-sell');
  const [language, setLanguage] = useState('en');
  const [locale, setLocale] = useState('US');

  // Custom topic state
  const [customTopicTitle, setCustomTopicTitle] = useState('');
  const [customTopicDescription, setCustomTopicDescription] = useState('');
  const [showCustomTopicInput, setShowCustomTopicInput] = useState(false);

  // Generated article
  const [generatedArticle, setGeneratedArticle] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Polling cleanup ref
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Clear polling on unmount or dialog close
  useEffect(() => {
    if (!open && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [open]);

  // Semantic suggestions state
  const [suggestedProducts, setSuggestedProducts] = useState<SuggestedProduct[]>([]);
  const [suggestedCategories, setSuggestedCategories] = useState<SuggestedCategory[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ products: Product[]; categories: string[] }>({ products: [], categories: [] });
  const [loadingSearch, setLoadingSearch] = useState(false);

  // AEO context state
  const [aeoContext, setAeoContext] = useState<AEOContext | null>(null);

  // Fetch existing articles and generate topic suggestions
  useEffect(() => {
    if (open && siteId) {
      fetchExistingArticles();
      fetchProducts();
      generateTopicSuggestions();
    }
  }, [open, siteId]);

  const fetchExistingArticles = async () => {
    try {
      const response = await authFetch(`/api/ai-writer/articles?siteId=${siteId}`);
      if (response.ok) {
        const articles = await response.json();
        setExistingArticles(articles);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await authFetch(`/api/ai-writer/products?siteId=${siteId}`);
      if (response.ok) {
        const data = await response.json();
        // Handle new response format with products, categories, and counts
        if (data.products && data.categories) {
          setProducts(data.products);
          setCategories(data.categories);
          // Store categories with product counts for topic selection UI
          if (data.categoriesWithCounts) {
            setCategoriesWithCounts(data.categoriesWithCounts);
          }
        } else {
          // Fallback for old format (just array of products)
          setProducts(data);
          const uniqueCategories = [...new Set(data.map((p: Product) => p.category).filter(Boolean))];
          setCategories(uniqueCategories as string[]);
        }
      } else {
        console.error('Error fetching products:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch semantically similar products/categories for the selected topic
  const fetchSemanticSuggestions = async (topic: TopicSuggestion) => {
    setLoadingSuggestions(true);
    try {
      const response = await authFetch('/api/ai-writer/semantic-match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          topicText: topic.title,
          topicDescription: topic.description,
          keywords: topic.keywords,
          relatedCategories: topic.relatedCategories || [], // NEW: Pass related categories for filtering
          limit: 10,
        }),
      });

      if (response.ok) {
        const { products: matchedProducts, categories: matchedCategories } = await response.json();
        setSuggestedProducts(matchedProducts || []);
        setSuggestedCategories(matchedCategories || []);

        // Auto-select top suggested products and categories
        if (matchedProducts?.length > 0) {
          const topProducts = matchedProducts
            .filter((p: SuggestedProduct) => p.relevance >= 0.7)
            .slice(0, 3)
            .map((p: SuggestedProduct) => p.id);
          setSelectedProducts(topProducts);
        }
        if (matchedCategories?.length > 0) {
          const topCategories = matchedCategories
            .filter((c: SuggestedCategory) => c.relevance >= 0.7)
            .slice(0, 2)
            .map((c: SuggestedCategory) => c.name);
          setSelectedCategories(topCategories);
        }
      }
    } catch (error) {
      console.error('Error fetching semantic suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Search products and categories by text
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoadingSearch(true);
    try {
      const response = await authFetch(`/api/ai-writer/search?siteId=${siteId}&query=${encodeURIComponent(searchQuery)}&limit=20`);

      if (response.ok) {
        const results = await response.json();
        setSearchResults({
          products: results.products || [],
          categories: results.categories || [],
        });
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoadingSearch(false);
    }
  };

  // Fetch AEO context for selected products/categories
  const fetchAEOContext = async (): Promise<AEOContext | null> => {
    try {
      const response = await authFetch('/api/ai-writer/aeo-context', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          productIds: selectedProducts,
          categoryPaths: selectedCategories,
        }),
      });

      if (response.ok) {
        const context = await response.json();
        setAeoContext(context);
        return context;
      }
    } catch (error) {
      console.error('Error fetching AEO context:', error);
    }
    return null;
  };

  const generateTopicSuggestions = async () => {
    setLoading(true);
    try {
      const response = await authFetch('/api/ai-writer/suggest-topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          persona: {
            name: persona.name,
            description: persona.description || persona.buying_behavior || '',
            characteristics: persona.characteristics || [
              persona.occupation,
              persona.age_range,
              persona.expectations,
              persona.pain_points
            ].filter(Boolean),
            avgOrderValue: persona.avgOrderValue,
            conversionRate: persona.conversionRate,
            // Include buyer persona specific fields
            buying_behavior: persona.buying_behavior,
            expectations: persona.expectations,
            pain_points: persona.pain_points,
            preferred_features: persona.preferred_features,
          },
          existingArticleTitles: existingArticles.map(a => ({
            title: a.title,
            summary: a.summary || a.content?.substring(0, 200),
          })),
        }),
      });

      if (response.ok) {
        const { topics } = await response.json();
        setSuggestedTopics(topics);
      } else {
        // Fallback topics if API fails
        setSuggestedTopics(generateFallbackTopics());
      }
    } catch (error) {
      console.error('Error generating topics:', error);
      setSuggestedTopics(generateFallbackTopics());
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackTopics = (): TopicSuggestion[] => {
    const baseTopics = {
      'premium-shoppers': [
        {
          id: '1',
          title: 'Luxury Toy Collections That Define Childhood Excellence',
          description: 'Explore premium toy collections that offer exceptional quality, educational value, and lasting memories for discerning parents.',
          relevanceScore: 95,
          keywords: ['luxury toys', 'premium quality', 'educational excellence'],
          targetIntent: 'research',
        },
        {
          id: '2',
          title: 'Investment-Worthy Educational Toys for Long-Term Development',
          description: 'Discover toys that grow with your child and provide years of developmental benefits, making them worth the premium price.',
          relevanceScore: 92,
          keywords: ['educational toys', 'child development', 'investment toys'],
          targetIntent: 'purchase',
        },
      ],
      'deal-hunters': [
        {
          id: '1',
          title: 'Hidden Toy Deals: Maximum Value Without Compromising Quality',
          description: 'Uncover the best toy deals that offer exceptional value while maintaining the quality your children deserve.',
          relevanceScore: 94,
          keywords: ['toy deals', 'best value', 'quality bargains'],
          targetIntent: 'purchase',
        },
        {
          id: '2',
          title: 'Seasonal Toy Sales Calendar: When to Buy for Maximum Savings',
          description: 'Master the art of timing your toy purchases to coincide with the deepest discounts throughout the year.',
          relevanceScore: 91,
          keywords: ['seasonal sales', 'discount calendar', 'savings guide'],
          targetIntent: 'research',
        },
      ],
      'research-enthusiasts': [
        {
          id: '1',
          title: 'Complete Guide to Age-Appropriate Toy Selection',
          description: 'A comprehensive analysis of developmental stages and the most suitable toys for each age group, backed by child psychology research.',
          relevanceScore: 96,
          keywords: ['age-appropriate', 'development stages', 'toy guide'],
          targetIntent: 'research',
        },
        {
          id: '2',
          title: 'Toy Safety Standards Explained: What Every Parent Should Know',
          description: 'Deep dive into toy safety certifications, materials, and testing standards to make informed purchasing decisions.',
          relevanceScore: 93,
          keywords: ['toy safety', 'certifications', 'safety standards'],
          targetIntent: 'research',
        },
      ],
    };

    // Return topics based on persona or default ones
    const personaKey = persona.name.toLowerCase().replace(/\s+/g, '-');
    return baseTopics[personaKey] || [
      {
        id: '1',
        title: `Best Toys for ${persona.name}`,
        description: `Curated selection of toys perfectly suited for ${persona.description.toLowerCase()}`,
        relevanceScore: 85,
        keywords: ['toys', persona.name.toLowerCase()],
        targetIntent: 'purchase',
      },
      {
        id: '2',
        title: `Gift Guide for ${persona.name}`,
        description: `Thoughtful gift ideas that resonate with ${persona.characteristics[0]?.toLowerCase() || 'this audience'}`,
        relevanceScore: 82,
        keywords: ['gift guide', 'recommendations'],
        targetIntent: 'research',
      },
      {
        id: '3',
        title: `Trending Products for ${persona.name}`,
        description: `Discover what's popular among ${persona.name.toLowerCase()} right now`,
        relevanceScore: 80,
        keywords: ['trending', 'popular products'],
        targetIntent: 'discovery',
      },
    ];
  };

  // Check if a topic has already been used (article exists with similar title)
  const isTopicUsed = (topic: TopicSuggestion): boolean => {
    return existingArticles.some(article => {
      const articleTitle = article.title?.toLowerCase() || '';
      const topicTitle = topic.title.toLowerCase();
      // Consider a topic used if titles match closely (fuzzy match)
      return articleTitle.includes(topicTitle.substring(0, 20)) || topicTitle.includes(articleTitle.substring(0, 20));
    });
  };

  const handleTopicSelect = (topic: TopicSuggestion) => {
    // Prevent selection of used topics
    if (isTopicUsed(topic)) {
      toast.error('This topic has already been used. Please select a different topic.');
      return;
    }

    setSelectedTopic(topic);
    setTargetKeywords(topic.keywords);
    setStep('config');
    // Fetch semantically similar products/categories for this topic
    fetchSemanticSuggestions(topic);
  };

  const handleUseCustomTopic = () => {
    if (!customTopicTitle.trim()) {
      toast.error('Please enter a topic title');
      return;
    }

    // Create a custom TopicSuggestion object
    const customTopic: TopicSuggestion = {
      id: `custom-${Date.now()}`,
      title: customTopicTitle.trim(),
      description: customTopicDescription.trim() || `Custom topic: ${customTopicTitle.trim()}`,
      relevanceScore: 100, // Custom topics are 100% relevant by definition
      keywords: [],
      targetIntent: 'custom',
    };

    // Check if this custom topic is similar to existing articles
    if (isTopicUsed(customTopic)) {
      toast.error('A similar topic has already been used. Please try a different topic.');
      return;
    }

    setSelectedTopic(customTopic);
    setTargetKeywords([]);
    setStep('config');
    // Fetch semantically similar products/categories for this custom topic
    fetchSemanticSuggestions(customTopic);

    // Reset custom topic inputs
    setCustomTopicTitle('');
    setCustomTopicDescription('');
    setShowCustomTopicInput(false);
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !targetKeywords?.includes(keywordInput.trim())) {
      setTargetKeywords([...(targetKeywords || []), keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setTargetKeywords((targetKeywords || []).filter(k => k !== keyword));
  };

  const handleGenerateArticle = async () => {
    if (!selectedTopic) return;

    setStep('generating');
    setLoading(true);

    try {
      // First, fetch AEO context for selected products/categories
      let aeoContent: AEOContext | null = null;
      if (selectedProducts.length > 0 || selectedCategories.length > 0) {
        aeoContent = await fetchAEOContext();
      }

      // POST to start background generation
      const response = await authFetch('/api/ai-writer/generate-persona', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          topic: selectedTopic,
          persona: {
            name: persona.name,
            description: persona.description || persona.buying_behavior || '',
            characteristics: persona.characteristics || [
              persona.occupation,
              persona.age_range,
              persona.expectations,
              persona.pain_points
            ].filter(Boolean),
            avgOrderValue: persona.avgOrderValue,
            conversionRate: persona.conversionRate,
            buying_behavior: persona.buying_behavior,
            expectations: persona.expectations,
            pain_points: persona.pain_points,
            preferred_features: persona.preferred_features,
          },
          selectedProducts,
          selectedCategories,
          targetKeywords,
          articleLength: parseInt(articleLength),
          tone,
          perspective,
          ctaStyle,
          language,
          locale,
          aeoContext: aeoContent,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to start article generation');
      }

      const { jobId } = await response.json();

      // Poll for completion
      pollIntervalRef.current = setInterval(async () => {
        try {
          const pollResponse = await authFetch(`/api/ai-writer/jobs/${jobId}`);
          if (!pollResponse.ok) throw new Error('Failed to check job status');

          const jobData = await pollResponse.json();

          if (jobData.status === 'completed' && jobData.article) {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setGeneratedArticle(jobData.article);
            setPreviewOpen(true);
            setLoading(false);
            toast.success('Article generated successfully!');
          } else if (jobData.status === 'failed') {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setLoading(false);
            toast.error(jobData.error_message || 'Article generation failed.');
            setStep('config');
          }
        } catch (pollError) {
          console.error('Polling error:', pollError);
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setLoading(false);
          toast.error('Lost connection while generating. Check the articles list.');
          setStep('config');
        }
      }, 3000);

    } catch (error) {
      console.error('Error starting article generation:', error);
      toast.error('Failed to start article generation. Please try again.');
      setStep('config');
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5" />
              AI Writer for {persona.name}
            </DialogTitle>
            <DialogDescription>
              Generate SEO-optimized content tailored for this customer segment
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {step === 'topics' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Select or Create Topic</h3>
                  <div className="flex gap-2">
                    <Button
                      variant={showCustomTopicInput ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setShowCustomTopicInput(!showCustomTopicInput)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {showCustomTopicInput ? 'Hide Custom' : 'Custom Topic'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={generateTopicSuggestions}
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Custom Topic Input */}
                {showCustomTopicInput && (
                  <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950/30">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Edit className="h-4 w-4 text-purple-600" />
                        Write Your Own Topic
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      <div>
                        <Label htmlFor="customTitle" className="text-sm font-medium">
                          Topic Title <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="customTitle"
                          placeholder="e.g., Best Budget Mountain Bikes Under $500"
                          value={customTopicTitle}
                          onChange={(e) => setCustomTopicTitle(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="customDescription" className="text-sm font-medium">
                          Description (Optional)
                        </Label>
                        <Textarea
                          id="customDescription"
                          placeholder="Briefly describe what this article should cover..."
                          value={customTopicDescription}
                          onChange={(e) => setCustomTopicDescription(e.target.value)}
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                      <Button
                        onClick={handleUseCustomTopic}
                        className="w-full"
                        disabled={!customTopicTitle.trim()}
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Use This Topic
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Catalog Summary - compact info about available products */}
                {categoriesWithCounts.length > 0 && (
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 flex items-center gap-2">
                    <FolderTree className="h-3.5 w-3.5 text-blue-600" />
                    <span>
                      Your catalog: <strong>{products.length}</strong> products across <strong>{categoriesWithCounts.length}</strong> categories.
                      Topics below show related product categories.
                    </span>
                  </div>
                )}

                <ScrollArea className="h-[380px] pr-4">
                  <div className="space-y-3">
                    {loading ? (
                      <div className="flex flex-col items-center justify-center py-12 space-y-4">
                        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                        <div className="text-center space-y-2">
                          <h3 className="text-lg font-semibold">Generating Topic Suggestions</h3>
                          <p className="text-sm text-gray-600">
                            Analyzing persona characteristics and creating tailored article ideas for <strong>{persona.name}</strong>...
                          </p>
                          <p className="text-xs text-gray-500">This may take a moment</p>
                        </div>
                      </div>
                    ) : (
                      suggestedTopics?.map((topic) => {
                        const isUsed = isTopicUsed(topic);
                        return (
                          <Card
                            key={topic.id}
                            className={
                              isUsed
                                ? 'opacity-60 bg-gray-50 dark:bg-gray-900 cursor-not-allowed border-gray-300'
                                : 'cursor-pointer hover:shadow-md transition-shadow'
                            }
                            onClick={() => handleTopicSelect(topic)}
                          >
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between">
                                <CardTitle className={`text-base ${isUsed ? 'text-gray-500' : ''}`}>
                                  {topic.title}
                                </CardTitle>
                                <div className="flex gap-2">
                                  {isUsed && (
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Used
                                    </Badge>
                                  )}
                                  <Badge variant="secondary">
                                    {topic.relevanceScore}% match
                                  </Badge>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <p className={`text-sm mb-3 ${isUsed ? 'text-gray-500' : 'text-gray-600'}`}>
                                {topic.description}
                              </p>

                              {/* Related Categories with Product Counts */}
                              {topic.relatedCategories && topic.relatedCategories.length > 0 && (
                                <div className="mb-3">
                                  <div className="flex items-center gap-1 mb-1">
                                    <Package className="h-3 w-3 text-blue-600" />
                                    <span className="text-xs text-blue-600 font-medium">Related Products:</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {topic.relatedCategories.slice(0, 4).map((category, idx) => {
                                      // Look up product count from categoriesWithCounts
                                      const categoryInfo = categoriesWithCounts.find(
                                        c => c.name === category ||
                                             c.name.endsWith(category) ||
                                             category.includes(c.name.split(' > ').pop() || '')
                                      );
                                      const productCount = categoryInfo?.productCount;
                                      return (
                                        <Badge key={idx} variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                          {category.split(' > ').pop()}
                                          {productCount && (
                                            <span className="ml-1 font-semibold text-blue-800">
                                              ({productCount})
                                            </span>
                                          )}
                                        </Badge>
                                      );
                                    })}
                                    {topic.relatedCategories.length > 4 && (
                                      <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                        +{topic.relatedCategories.length - 4} more
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center justify-between">
                                <div className="flex flex-wrap gap-1">
                                  {topic.keywords?.map((keyword) => (
                                    <Badge key={keyword} variant="outline" className="text-xs">
                                      {keyword}
                                    </Badge>
                                  )) || []}
                                </div>
                                {!isUsed && <ChevronRight className="h-4 w-4 text-gray-400" />}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}

            {step === 'config' && selectedTopic && (
              <Tabs defaultValue="products" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="products">
                    <Package className="h-4 w-4 mr-2" />
                    Products
                  </TabsTrigger>
                  <TabsTrigger value="keywords">
                    <Target className="h-4 w-4 mr-2" />
                    Keywords
                  </TabsTrigger>
                  <TabsTrigger value="style">
                    <FileText className="h-4 w-4 mr-2" />
                    Style
                  </TabsTrigger>
                </TabsList>

                <ScrollArea className="h-[400px] mt-4">
                  <TabsContent value="products" className="space-y-4">
                    {/* AI Suggested Products Section */}
                    {loadingSuggestions ? (
                      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30">
                        <CardContent className="py-4">
                          <div className="flex items-center gap-3">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                            <span className="text-sm text-blue-700 dark:text-blue-300">
                              Finding best products for "{selectedTopic?.title}"...
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ) : suggestedProducts.length > 0 && (
                      <Card className="border-green-200 bg-green-50 dark:bg-green-950/30">
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-green-600" />
                            Suggested Products for This Topic
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          {suggestedProducts.slice(0, 5).map((product) => (
                            <div key={product.id} className="flex items-center justify-between p-2 rounded-md bg-white dark:bg-gray-800 border">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={`suggested-${product.id}`}
                                  checked={selectedProducts.includes(product.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedProducts([...selectedProducts, product.id]);
                                    } else {
                                      setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                                    }
                                  }}
                                />
                                <div>
                                  <label
                                    htmlFor={`suggested-${product.id}`}
                                    className="text-sm font-medium cursor-pointer"
                                  >
                                    {product.name}
                                  </label>
                                  <p className="text-xs text-muted-foreground">{product.category}</p>
                                </div>
                              </div>
                              <Badge variant="secondary" className="text-xs">
                                {Math.round(product.relevance * 100)}% match
                              </Badge>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {/* AI Suggested Categories Section */}
                    {suggestedCategories.length > 0 && !loadingSuggestions && (
                      <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950/30">
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <FolderTree className="h-4 w-4 text-purple-600" />
                            Suggested Categories for This Topic
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 flex flex-wrap gap-2">
                          {suggestedCategories.slice(0, 5).map((category) => (
                            <Badge
                              key={category.name}
                              variant={selectedCategories.includes(category.name) ? 'default' : 'outline'}
                              className="cursor-pointer py-1.5 px-3"
                              onClick={() => {
                                if (selectedCategories.includes(category.name)) {
                                  setSelectedCategories(selectedCategories.filter(c => c !== category.name));
                                } else {
                                  setSelectedCategories([...selectedCategories, category.name]);
                                }
                              }}
                            >
                              {selectedCategories.includes(category.name) ? (
                                <X className="h-3 w-3 mr-1" />
                              ) : (
                                <Plus className="h-3 w-3 mr-1" />
                              )}
                              {category.name}
                              <span className="ml-1.5 text-xs opacity-70">
                                {Math.round(category.relevance * 100)}%
                              </span>
                            </Badge>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {/* Search Section */}
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Search className="h-4 w-4" />
                          Search Products & Categories
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-3">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Search by name, brand, or category..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          />
                          <Button onClick={handleSearch} size="sm" disabled={loadingSearch}>
                            {loadingSearch ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Search className="h-4 w-4" />
                            )}
                          </Button>
                        </div>

                        {/* Search Results */}
                        {(searchResults.products.length > 0 || searchResults.categories.length > 0) && (
                          <div className="space-y-2 border rounded-lg p-3 bg-muted/50">
                            {searchResults.products.length > 0 && (
                              <div>
                                <Label className="text-xs text-muted-foreground mb-2 block">Products</Label>
                                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                  {searchResults.products.slice(0, 10).map((product) => (
                                    <div key={product.id} className="flex items-center space-x-2">
                                      <Checkbox
                                        id={`search-${product.id}`}
                                        checked={selectedProducts.includes(product.id)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setSelectedProducts([...selectedProducts, product.id]);
                                          } else {
                                            setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                                          }
                                        }}
                                      />
                                      <label
                                        htmlFor={`search-${product.id}`}
                                        className="text-sm leading-none cursor-pointer"
                                      >
                                        {product.name}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {searchResults.categories.length > 0 && (
                              <div>
                                <Label className="text-xs text-muted-foreground mb-2 block">Categories</Label>
                                <div className="flex flex-wrap gap-1.5">
                                  {searchResults.categories.slice(0, 10).map((category) => (
                                    <Badge
                                      key={category}
                                      variant={selectedCategories.includes(category) ? 'default' : 'outline'}
                                      className="cursor-pointer text-xs"
                                      onClick={() => {
                                        if (selectedCategories.includes(category)) {
                                          setSelectedCategories(selectedCategories.filter(c => c !== category));
                                        } else {
                                          setSelectedCategories([...selectedCategories, category]);
                                        }
                                      }}
                                    >
                                      {category}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Selected Items Summary */}
                    {(selectedProducts.length > 0 || selectedCategories.length > 0) && (
                      <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30">
                        <CardContent className="py-3">
                          <div className="flex flex-wrap gap-2">
                            {selectedProducts.length > 0 && (
                              <Badge variant="outline" className="text-amber-700">
                                <Package className="h-3 w-3 mr-1" />
                                {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
                              </Badge>
                            )}
                            {selectedCategories.length > 0 && (
                              <Badge variant="outline" className="text-amber-700">
                                <FolderTree className="h-3 w-3 mr-1" />
                                {selectedCategories.length} categor{selectedCategories.length > 1 ? 'ies' : 'y'} selected
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* All Products (collapsed by default if suggestions exist) */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        All Products
                      </Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                        {products?.map((product) => (
                          <div key={product.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={product.id}
                              checked={selectedProducts.includes(product.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedProducts([...selectedProducts, product.id]);
                                } else {
                                  setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                                }
                              }}
                            />
                            <label
                              htmlFor={product.id}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {product.name} - ${product.price}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        All Categories
                      </Label>
                      <div className="space-y-2 max-h-32 overflow-y-auto border rounded-lg p-3">
                        {categories?.map((category) => (
                          <div key={category} className="flex items-center space-x-2">
                            <Checkbox
                              id={category}
                              checked={selectedCategories.includes(category)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCategories([...selectedCategories, category]);
                                } else {
                                  setSelectedCategories(selectedCategories.filter(c => c !== category));
                                }
                              }}
                            />
                            <label
                              htmlFor={category}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {category}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="keywords" className="space-y-4">
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        Target Keywords
                      </Label>
                      <div className="flex gap-2 mb-3">
                        <Input
                          placeholder="Add a keyword..."
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                        />
                        <Button onClick={handleAddKeyword} size="sm">
                          Add
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {targetKeywords?.map((keyword) => (
                          <Badge
                            key={keyword}
                            variant="secondary"
                            className="cursor-pointer"
                            onClick={() => handleRemoveKeyword(keyword)}
                          >
                            {keyword} ×
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="style" className="space-y-4">
                    <div>
                      <Label htmlFor="length">Article Length (words)</Label>
                      <Select value={articleLength} onValueChange={setArticleLength}>
                        <SelectTrigger id="length">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="800">Short (800 words)</SelectItem>
                          <SelectItem value="1200">Medium (1200 words)</SelectItem>
                          <SelectItem value="1500">Long (1500 words)</SelectItem>
                          <SelectItem value="2000">Extra Long (2000 words)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="tone">Tone</Label>
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger id="tone">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="authoritative">Authoritative</SelectItem>
                          <SelectItem value="inspirational">Inspirational</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="perspective">Perspective</Label>
                      <Select value={perspective} onValueChange={setPerspective}>
                        <SelectTrigger id="perspective">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="first-person">First Person (I/We)</SelectItem>
                          <SelectItem value="second-person">Second Person (You)</SelectItem>
                          <SelectItem value="third-person">Third Person (They)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="cta">Call-to-Action Style</Label>
                      <Select value={ctaStyle} onValueChange={setCtaStyle}>
                        <SelectTrigger id="cta">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="soft-sell">Soft Sell</SelectItem>
                          <SelectItem value="direct">Direct</SelectItem>
                          <SelectItem value="educational">Educational</SelectItem>
                          <SelectItem value="urgency">Urgency-Based</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="language">Language</Label>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger id="language">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Español (Spanish)</SelectItem>
                          <SelectItem value="fr">Français (French)</SelectItem>
                          <SelectItem value="de">Deutsch (German)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="locale">Locale / Region</Label>
                      <Select value={locale} onValueChange={setLocale}>
                        <SelectTrigger id="locale">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">United States (USD, $)</SelectItem>
                          <SelectItem value="GB">United Kingdom (GBP, £)</SelectItem>
                          <SelectItem value="EU">European Union (EUR, €)</SelectItem>
                          <SelectItem value="ES">Spain (EUR, €)</SelectItem>
                          <SelectItem value="FR">France (EUR, €)</SelectItem>
                          <SelectItem value="DE">Germany (EUR, €)</SelectItem>
                          <SelectItem value="MX">Mexico (MXN, $)</SelectItem>
                          <SelectItem value="CA">Canada (CAD, $)</SelectItem>
                          <SelectItem value="AU">Australia (AUD, $)</SelectItem>
                          <SelectItem value="JP">Japan (JPY, ¥)</SelectItem>
                          <SelectItem value="CN">China (CNY, ¥)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                </ScrollArea>

                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep('topics')}>
                    Back
                  </Button>
                  <Button onClick={handleGenerateArticle} disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Article
                      </>
                    )}
                  </Button>
                </div>
              </Tabs>
            )}

            {step === 'generating' && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Generating Your Article</h3>
                <p className="text-sm text-gray-600 text-center max-w-md">
                  Creating SEO-optimized content tailored for {persona.name}. This may take a moment...
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {generatedArticle && (
        <AIWriterPreview
          open={previewOpen}
          onOpenChange={(open) => {
            setPreviewOpen(open);
            if (!open) {
              // Reset the step when preview closes
              setStep('topics');
              setLoading(false);
            }
          }}
          article={generatedArticle}
          onPublishSuccess={() => {
            // Close both modals after successful publish
            setPreviewOpen(false);
            setStep('topics');
            setLoading(false);
            onOpenChange(false);
            toast.success('Article published successfully!');
          }}
        />
      )}
    </>
  );
}